// src/utils/startTimeCalculator.js - Start Time Calculation for Events

/**
 * Calculate start times for events based on dance timing rules
 *
 * Timing Rules:
 * - Each dance takes 1 minute 30 seconds (90 seconds)
 * - 10 second break between dances
 * - 10 minute break between events (except lunch/predefined breaks)
 */

const DANCE_DURATION = 90; // 1 minute 30 seconds in seconds
const BREAK_BETWEEN_DANCES = 10; // 10 seconds in seconds
const BREAK_BETWEEN_EVENTS = 600; // 10 minutes in seconds

/**
 * Helper function to detect non-dance events
 * @param {Object} event - Event object
 * @returns {boolean} True if this is a non-dance event
 */
const isNonDanceEvent = (event) => {
  // Check explicit flag first (for future events created with the new form)
  if (event.isNonDanceEvent === true) {
    return true;
  }

  // Legacy detection: events with no dances, no age/level/style info are likely non-dance events
  const hasNoDances = (!event.dances || event.dances.length === 0);
  const hasNullOrMissingDanceInfo = (
    (event.ageGroup === null || event.ageGroup === undefined || event.ageGroup === "") &&
    (event.level === null || event.level === undefined || event.level === "") &&
    (event.danceStyle === null || event.danceStyle === undefined || event.danceStyle === "")
  );
  const hasNoDancerInfo = (!event.dancers || event.dancers.length === 0);

  // Also check if the name suggests it's a break/non-dance event
  const nameIndicatesNonDance = event.name && (
    event.name.toUpperCase().includes('BREAK') ||
    event.name.toUpperCase().includes('LUNCH') ||
    event.name.toUpperCase().includes('INTERVAL') ||
    event.name.toUpperCase().includes('ENTERTAINMENT') ||
    event.name.toUpperCase().includes('OPENING') ||
    event.name.toUpperCase().includes('CLOSING')
  );

  // If the name clearly indicates it's a non-dance event, prioritize that over dancer assignment
  if (nameIndicatesNonDance && hasNoDances) {
    return true;
  }

  return hasNoDances && hasNullOrMissingDanceInfo && hasNoDancerInfo;
};

/**
 * Calculate the total duration for an event in seconds
 * @param {Object} event - The event object
 * @returns {number} Total duration in seconds
 */
export const calculateEventDuration = (event) => {
  // Check if this is a non-dance event
  if (event.isNonDanceEvent === true || isNonDanceEvent(event)) {
    // Use custom duration if provided, otherwise use default 15 minutes
    const duration = event.customDuration || 15;
    return duration * 60; // Convert minutes to seconds
  }

  const dances = event.dances || [];
  const numDances = dances.length;

  if (numDances === 0) {
    // For events with no dances but not marked as non-dance, use minimum duration
    return 0;
  }

  // Calculate dance time: numDances * 1min 30sec
  const danceTime = numDances * DANCE_DURATION;

  // Calculate break time: (numDances - 1) * 10sec between dances
  const danceBreakTime = (numDances - 1) * BREAK_BETWEEN_DANCES;

  return danceTime + danceBreakTime;
};

/**
 * Calculate start times for all events in sequence
 * @param {Array} events - Array of events sorted by itemNumber
 * @param {Date} competitionStartTime - Start time of the competition (default: 9:00 AM)
 * @param {Array} lunchBreaks - Array of lunch break objects {time, duration}
 * @param {Array} predefinedBreaks - Array of predefined break objects {time, duration}
 * @returns {Array} Events with calculated start times
 */
export const calculateStartTimes = (
  events,
  competitionStartTime = null,
  lunchBreaks = [],
  predefinedBreaks = []
) => {
  // Default competition start time: 9:00 AM
  const startTime = competitionStartTime || new Date();
  startTime.setHours(9, 0, 0, 0);

  const eventsWithStartTimes = [];
  let currentTime = new Date(startTime);

  // Sort events by item number to ensure proper sequence
  const sortedEvents = [...events].sort((a, b) => {
    const itemA = a.itemNumber || 999;
    const itemB = b.itemNumber || 999;
    return itemA - itemB;
  });

  sortedEvents.forEach((event, index) => {
    // Check for lunch break or predefined break at this time
    const breakInfo = checkForBreak(currentTime, lunchBreaks, predefinedBreaks);
    if (breakInfo) {
      // Add break duration to current time
      currentTime = new Date(currentTime.getTime() + breakInfo.duration * 1000);
    }

    // Set start time for this event
    const eventWithStartTime = {
      ...event,
      startTime: new Date(currentTime),
      estimatedDuration: calculateEventDuration(event)
    };

    eventsWithStartTimes.push(eventWithStartTime);

    // Add event duration to current time for next event
    const eventDuration = calculateEventDuration(event);
    currentTime = new Date(currentTime.getTime() + eventDuration * 1000);

    // Add break between events (10 minutes)
    if (index < sortedEvents.length - 1) {
      currentTime = new Date(currentTime.getTime() + BREAK_BETWEEN_EVENTS * 1000);
    }
  });

  return eventsWithStartTimes;
};

/**
 * Calculate and return start times for events without modifying original data
 * @param {Array} events - Array of events sorted by itemNumber
 * @param {string|Date} competitionStartTime - Competition official start time
 * @returns {Array} Events with calculated startTime property
 */
export const getEventsWithStartTimes = (events, competitionStartTime) => {
  if (!competitionStartTime) {
    return events;
  }

  const startTime = typeof competitionStartTime === 'string'
    ? new Date(competitionStartTime)
    : competitionStartTime;

  return calculateStartTimes(events, startTime);
};

/**
 * Check if current time conflicts with any lunch or predefined breaks
 * @param {Date} currentTime - Current time to check
 * @param {Array} lunchBreaks - Lunch breaks array
 * @param {Array} predefinedBreaks - Predefined breaks array
 * @returns {Object|null} Break info {duration} or null
 */
const checkForBreak = (currentTime, lunchBreaks, predefinedBreaks) => {
  const allBreaks = [...lunchBreaks, ...predefinedBreaks];

  for (const breakInfo of allBreaks) {
    if (breakInfo.time) {
      const breakTime = new Date(breakInfo.time);

      // Check if current time is at or after break time
      if (currentTime >= breakTime) {
        return {
          duration: breakInfo.duration || BREAK_BETWEEN_EVENTS,
          type: breakInfo.type || 'scheduled'
        };
      }
    }
  }

  return null;
};

/**
 * Format time for display (HH:MM format)
 * @param {Date} date - Date object
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  if (!date) return '--:--';

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Calculate estimated end time for an event
 * @param {Object} event - Event with startTime and estimatedDuration
 * @returns {Date|null} End time
 */
export const calculateEventEndTime = (event) => {
  if (!event.startTime || !event.estimatedDuration) {
    return null;
  }

  const endTime = new Date(event.startTime.getTime() + event.estimatedDuration * 1000);
  return endTime;
};

/**
 * Save official start time to competition in the database
 * @param {string} competitionId - Competition ID to update
 * @param {Date} officialStartTime - Official competition start time
 * @param {string} host - API host
 * @param {string} company - Company identifier
 * @returns {Promise} Resolves when competition is updated
 */
export const saveCompetitionStartTime = async (competitionId, officialStartTime, host, company) => {
  return fetch(`https://${host}:6003/api/${company}/competitions/${competitionId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      official_start_time: officialStartTime.toISOString()
    })
  });
};

/**
 * Generate a summary of the competition schedule
 * @param {Array} eventsWithStartTimes - Events with calculated start times
 * @returns {Object} Schedule summary
 */
export const generateScheduleSummary = (eventsWithStartTimes) => {
  if (eventsWithStartTimes.length === 0) {
    return {
      totalEvents: 0,
      totalDuration: 0,
      startTime: null,
      endTime: null,
      estimatedEndTime: null
    };
  }

  const firstEvent = eventsWithStartTimes[0];
  const lastEvent = eventsWithStartTimes[eventsWithStartTimes.length - 1];

  const totalDanceTime = eventsWithStartTimes.reduce((sum, event) => {
    return sum + (event.estimatedDuration || 0);
  }, 0);

  const totalBreakTime = (eventsWithStartTimes.length - 1) * BREAK_BETWEEN_EVENTS;
  const totalDuration = totalDanceTime + totalBreakTime;

  const endTime = calculateEventEndTime(lastEvent);

  return {
    totalEvents: eventsWithStartTimes.length,
    totalDanceTime,
    totalBreakTime,
    totalDuration,
    startTime: firstEvent.startTime,
    endTime,
    estimatedEndTime: endTime
  };
};